import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, TokenValidator } from "@/lib/api-client";
import { SecureTokenManager } from "@/lib/secure-token-manager";
import { API_ENDPOINTS } from "@/lib/constants";
import type {
  LoginRequest,
  LoginResponse,
  User,
  ValidateTokenResponse,
} from "@/types/api";

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  validateToken: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Use window.location for navigation since this provider may be outside RouterProvider
  const navigate = useCallback((path: string) => {
    window.location.href = path;
  }, []);

  // Load user from secure storage on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        // Check if user is authenticated via httpOnly cookies
        const isAuthenticated = TokenValidator.isAuthenticated();
        const savedUser = SecureTokenManager.getNonSensitiveData();

        if (isAuthenticated && savedUser) {
          setUser(savedUser as unknown as User);
          // Validate token in background
          await validateToken();
        } else {
          // Clear any residual data
          SecureTokenManager.clearAuth();
          setUser(null);
        }
      } catch (error) {
        console.error("Failed to load user:", error);
        SecureTokenManager.clearAuth();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      try {
        setIsLoading(true);
        const response = await api.post<LoginResponse>(
          API_ENDPOINTS.LOGIN,
          credentials,
        );

        if (response.data.success && response.data.data) {
          const { user, token } = response.data.data;

          // Store JWT token in sessionStorage for WebSocket authentication
          // Note: httpOnly cookie protects API calls from XSS, but WebSocket needs token in auth object
          if (token) {
            SecureTokenManager.setToken(token);
          }

          // Store non-sensitive user data in sessionStorage
          SecureTokenManager.setNonSensitiveData({
            id: user.id,
            username: user.username,
            email: user.email,
            role: user.role,
            status: user.status,
          });

          setUser(user);

          // Navigate to dashboard
          navigate("/");
        }
      } catch (error) {
        console.error("Login failed:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [navigate],
  );

  const logout = useCallback(async () => {
    try {
      // Call logout endpoint (server will clear httpOnly cookies)
      await api.post(API_ENDPOINTS.LOGOUT);
    } catch (error) {
      console.error("Logout request failed:", error);
      // Continue with logout even if request fails
    } finally {
      // Clear client-side data
      SecureTokenManager.clearAuth();
      setUser(null);
      navigate("/login");
    }
  }, [navigate]);

  const validateToken = useCallback(async (): Promise<boolean> => {
    try {
      // Check authentication status via secure method
      const isAuthenticated = TokenValidator.isAuthenticated();
      if (!isAuthenticated) {
        SecureTokenManager.clearAuth();
        setUser(null);
        return false;
      }

      // Validate token with server
      const response = await api.post<ValidateTokenResponse>(
        API_ENDPOINTS.VALIDATE,
      );

      if (response.data.success && response.data.data?.valid) {
        // Token is valid, update user data if needed
        const userData = response.data.data.user;
        const updatedUser = {
          id: userData.userId,
          username: userData.username,
          email: userData.email,
          role: userData.role,
          status: "active",
        };

        setUser(updatedUser);
        // Update non-sensitive data storage
        SecureTokenManager.setNonSensitiveData(updatedUser);
        return true;
      }

      // Token is invalid
      SecureTokenManager.clearAuth();
      setUser(null);
      return false;
    } catch (error) {
      console.error("Token validation failed:", error);
      SecureTokenManager.clearAuth();
      setUser(null);
      return false;
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      logout,
      validateToken,
    }),
    [user, isLoading, login, logout, validateToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
